import type { RefObject } from "react";
import { useState, useCallback, useRef } from "react";

/**
 * Interface for the global category scroll functionality
 */
export interface CategoryScrollHook {
  // Register scroll container and category refs
  registerScrollContainer: (ref: RefObject<HTMLDivElement | null>) => void;
  registerCategoryRef: (categoryId: string, ref: HTMLDivElement | null) => void;

  // Scroll functionality
  scrollToCategory: (categoryId: string) => void;
  calculateStickyHeaderHeight: () => number;

  // State management
  activeCategoryId: string | null;
  setActiveCategoryId: (id: string | null) => void;

  // Manual navigation control
  setManualNavigation: (flag: boolean) => void;
}

/**
 * Global category scroll hook
 * Provides scrolling functionality that can be shared across components
 */
export const useCategoryScroll = (): CategoryScrollHook => {
  // State for active category tracking
  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);

  // Refs for scroll container and category elements
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const categoryRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const manualNavigationRef = useRef(false);

  // Register scroll container
  const registerScrollContainer = useCallback(
    (ref: RefObject<HTMLDivElement | null>) => {
      scrollContainerRef.current = ref.current;
    },
    []
  );

  // Register category reference
  const registerCategoryRef = useCallback(
    (categoryId: string, ref: HTMLDivElement | null) => {
      categoryRefs.current[categoryId] = ref;
    },
    []
  );

  // Calculate sticky header height
  const calculateStickyHeaderHeight = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return 120; // fallback height

    // Find the sticky header element (the Box with position: sticky)
    const stickyHeader = container.querySelector('[data-sticky-header="true"]');
    if (stickyHeader) {
      return stickyHeader.getBoundingClientRect().height;
    }

    // Fallback: calculate based on breakpoint
    const isMobile = window.innerWidth < 900; // x2l breakpoint
    const headerHeight = isMobile ? 56 : 64;
    const categoryNavHeight = 60; // approximate height including padding
    return headerHeight + categoryNavHeight;
  }, []);

  // Handle category scrolling
  const scrollToCategory = useCallback(
    (categoryId: string) => {
      // Set manual navigation flag to prevent intersection observer updates
      manualNavigationRef.current = true;

      // Update active category immediately
      setActiveCategoryId(categoryId);

      const categoryElement = categoryRefs.current[categoryId];
      const container = scrollContainerRef.current;

      if (categoryElement && container) {
        const headerHeight = calculateStickyHeaderHeight();
        const elementTop = categoryElement.offsetTop;

        container.scrollTo({
          top: Math.max(0, elementTop - headerHeight),
          behavior: "smooth",
        });
      }

      // Reset manual navigation flag after scroll completes
      setTimeout(() => {
        manualNavigationRef.current = false;
      }, 1000);
    },
    [calculateStickyHeaderHeight]
  );

  // Set manual navigation flag
  const setManualNavigation = useCallback((flag: boolean) => {
    manualNavigationRef.current = flag;
  }, []);

  return {
    registerScrollContainer,
    registerCategoryRef,
    scrollToCategory,
    calculateStickyHeaderHeight,
    activeCategoryId,
    setActiveCategoryId,
    setManualNavigation,
  };
};
